import { useState } from 'react';

export type OAuthProvider = 'google' | 'apple' | 'github';

interface UseOAuthLoadingReturn {
  loadingStates: Record<OAuthProvider, boolean>;
  setLoading: (provider: OAuthProvider, loading: boolean) => void;
  isLoading: (provider: OAuthProvider) => boolean;
  anyLoading: () => boolean;
}

/**
 * Custom hook for managing OAuth provider loading states
 *
 * @returns Object with loading state management functions
 *
 * @example
 * const { setLoading, isLoading, anyLoading } = useOAuthLoading();
 *
 * const handleLogin = async () => {
 *   setLoading('google', true);
 *   try {
 *     await oauthLogin('google', token);
 *   } finally {
 *     setLoading('google', false);
 *   }
 * };
 */
export function useOAuthLoading(): UseOAuthLoadingReturn {
  const [loadingStates, setLoadingStates] = useState<Record<OAuthProvider, boolean>>({
    google: false,
    apple: false,
    github: false,
  });

  const setLoading = (provider: OAuthProvider, loading: boolean) => {
    setLoadingStates(prev => ({ ...prev, [provider]: loading }));
  };

  const isLoading = (provider: OAuthProvider) => loadingStates[provider];

  const anyLoading = () => Object.values(loadingStates).some(state => state);

  return { loadingStates, setLoading, isLoading, anyLoading };
}
